import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, FolderOpen, Trash, ArrowRight } from "@phosphor-icons/react";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import IntroInbox from "../components/IntroInbox";
import AdsCards from "../components/AdsCards";
import RetentionBanner from "../components/RetentionBanner";
import ReadinessGauge from "../components/ReadinessGauge";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { useMarketplaceEnabled } from "../context/ConfigContext";
import { AlertTriangle, Clock } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  review: "bg-amber-50 text-amber-800 border-amber-200",
  finalized: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function Dashboard() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [consent, setConsent] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const marketplaceEnabled = useMarketplaceEnabled();

  // Handle Stripe return: ?session_id=... → poll status (marketplace only)
  useEffect(() => {
    if (!marketplaceEnabled) return;
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 6;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data?.payment_status === "paid") {
          if (r.data.activated === "subscription") {
            track("payment_completed_sub", { session_id: sessionId });
            toast.success("Subscription active. Welcome to the Attorney Portal");
          } else if (r.data.billing_type === "unlock") {
            track("payment_completed_unlock", { session_id: sessionId, case_id: r.data.case_id });
            toast.success("Unlock purchased. Intro request sent to the case owner");
          } else {
            toast.success("Payment confirmed");
          }
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (r.data?.status === "expired") {
          toast.error("Payment session expired");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast("Payment still processing. Refresh later to confirm");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
        }
      } catch (e) {
        toast.error("Could not verify payment");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplaceEnabled]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cases");
      setCases(r.data);
    } catch (e) {
      toast.error("Could not load cases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setNewName("");
    setConsent(false);
  };

  const createCase = async () => {
    if (!newName.trim()) return toast.error("Please enter a case name");
    if (!consent) return toast.error("Please acknowledge the consent notice to continue");
    try {
      const r = await api.post("/cases", { name: newName.trim(), consent_acknowledged: true });
      track("case_created", { case_id: r.data.case_id });
      setOpen(false);
      resetForm();
      window.location.href = `/case/${r.data.case_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create case");
    }
  };

  const removeCase = async (id) => {
    if (!window.confirm("Delete this case? This cannot be undone.")) return;
    try {
      await api.delete(`/cases/${id}`);
      toast.success("Case deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const pending = user?.deletion_pending;

  return (
    <div className="min-h-screen bg-white">
      <DisclaimerBanner />
      <AppHeader />

      <main className="max-w-[1400px] mx-auto px-6 py-12" data-testid="dashboard">
        <RetentionBanner retention={user?.retention} />
        <ReadinessGauge mode="full" />
        {pending && (
          <div
            className="mb-8 border border-red-300 bg-red-50 px-5 py-4 text-sm text-red-900 flex items-start gap-3"
            data-testid="deletion-pending-banner"
          >
            <Clock className="w-4 h-4 mt-0.5 shrink-0 text-red-700" />
            <div className="flex-1">
              <div className="font-medium">Account deletion pending</div>
              <div className="text-red-800">
                Your data is scheduled to be permanently erased on{" "}
                <strong>{new Date(pending.scheduled_at).toLocaleDateString()}</strong>. You can cancel from your Account
                page.
              </div>
            </div>
            <Link
              to="/account"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-900 hover:bg-red-100 text-xs font-mono-tech uppercase tracking-wider"
            >
              Manage
            </Link>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-8">
            <div className="label-tiny mb-3">Workspace</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-light text-slate-900 leading-tight">
              Your <em className="not-italic font-semibold">cases</em>.
            </h1>
            <p className="text-slate-600 mt-2 max-w-xl">
              Create a new matter to start drafting, or open an existing case to continue where you left off.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end items-end">
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-case-btn" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
                  <Plus className="w-4 h-4 mr-2" /> New matter
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-slate-300" data-testid="new-case-dialog">
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-2xl">Start a new matter</DialogTitle>
                  <DialogDescription>Give your matter a working title. You can rename it later.</DialogDescription>
                </DialogHeader>
                <div>
                  <label className="label-tiny block mb-2">Case Name</label>
                  <Input
                    placeholder="e.g., Doe v. Acme Corp."
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && consent && createCase()}
                    data-testid="new-case-name-input"
                    className="rounded-none border-slate-300"
                  />
                </div>
                <label
                  htmlFor="new-case-consent"
                  className="flex items-start gap-3 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 cursor-pointer select-none"
                  data-testid="new-case-consent"
                >
                  <input
                    id="new-case-consent"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5"
                    data-testid="new-case-consent-checkbox"
                  />
                  <span className="flex items-start gap-1.5 leading-relaxed">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-700 shrink-0" />
                    I understand ProSe.Claims is not a law firm and does not provide legal advice. The documents I create
                    are for my own use and require independent review before filing.
                  </span>
                </label>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }} className="rounded-none border-slate-300">Cancel</Button>
                  <Button
                    onClick={createCase}
                    disabled={!consent || !newName.trim()}
                    data-testid="create-case-confirm"
                    className="rounded-none bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
                  >
                    Create matter
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="border border-slate-200 p-12 text-center label-tiny">Loading…</div>
        ) : cases.length === 0 ? (
          <div
            className="border border-slate-200 p-16 text-center bg-grid-subtle"
            data-testid="empty-state"
          >
            <FolderOpen weight="duotone" className="w-10 h-10 mx-auto text-slate-400" />
            <div className="font-serif-display text-2xl mt-4">No matters yet</div>
            <p className="text-slate-600 mt-2">Click &quot;New matter&quot; above to begin drafting your first complaint.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {cases.map((c, i) => (
              <div
                key={c.case_id}
                className={`group relative border border-slate-200 ${i % 3 !== 0 ? "md:border-l-0" : ""} -mt-px md:mt-0 hover:bg-slate-50 transition-colors p-6`}
                data-testid={`case-card-${c.case_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className={`text-[10px] font-mono-tech uppercase tracking-wider px-2 py-1 border ${STATUS_STYLE[c.status] || STATUS_STYLE.draft}`}>
                    {c.status || "draft"}
                  </span>
                  <button
                    onClick={() => removeCase(c.case_id)}
                    className="p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-700"
                    aria-label="Delete case"
                    data-testid={`delete-case-${c.case_id}`}
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
                <Link to={`/case/${c.case_id}`} data-testid={`open-case-${c.case_id}`}>
                  <h3 className="font-serif-display text-2xl text-slate-900 leading-tight mb-2 group-hover:underline">
                    {c.name}
                  </h3>
                </Link>
                <div className="text-sm text-slate-500 mb-6 tabular-nums">
                  Updated {new Date(c.updated_at).toLocaleDateString()} · {c.timeline?.length || 0} events · {c.evidence?.length || 0} exhibits
                </div>
                <Link
                  to={`/case/${c.case_id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 hover:gap-2 transition-all"
                >
                  Open matter <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        )}

        {marketplaceEnabled && <IntroInbox />}
        {marketplaceEnabled && <AdsCards placement="dashboard" />}
      </main>
      <SiteFooter />
    </div>
  );
}
